const KEYS = {
  idToken: 'iqmaxx:idToken',
  // What sign-out hands Cognito (POST /auth/logout). The API's Bearer is the ID
  // token, and GlobalSignOut refuses that one.
  accessToken: 'iqmaxx:accessToken',
  refreshToken: 'iqmaxx:refreshToken',
  expiry: 'iqmaxx:tokenExpiry', // epoch ms when idToken expires
};

const DEVICE_KEY = 'iqmaxx:deviceId';

/** Held for the page's life when storage is blocked. */
let unstoredDeviceId: string | null = null;

const newDeviceId = (): string => {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Not a credential (see getDeviceId), so a plain fallback is enough here.
  return `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

/**
 * §VI.1 — which device this browser claims to be. Not in KEYS on purpose: it
 * outlives a sign-out, so a new sign-in on this device lands back on the
 * kid-mode lock it left (DEC-S2-S01-06).
 *
 * Not a credential, and the lock does not depend on it. The server binds the
 * lock to the sign-in the verified token names; this value can only add a lock,
 * never lift one. Deleting it here just mints a new one: the tokens stay, and
 * the child's space stays locked until the Parent re-authenticates.
 */
export function getDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    if (stored) return stored;
    const created = newDeviceId();
    localStorage.setItem(DEVICE_KEY, created);
    return created;
  } catch {
    unstoredDeviceId = unstoredDeviceId ?? newDeviceId();
    return unstoredDeviceId;
  }
}

export function saveTokens({
  idToken,
  accessToken,
  refreshToken,
  expiresIn,
}: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}): void {
  localStorage.setItem(KEYS.idToken, idToken);
  localStorage.setItem(KEYS.accessToken, accessToken);
  localStorage.setItem(KEYS.refreshToken, refreshToken);
  localStorage.setItem(KEYS.expiry, String(Date.now() + expiresIn * 1000));
}

export function getStoredTokens(): {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiry: number | null;
} {
  return {
    idToken: localStorage.getItem(KEYS.idToken),
    accessToken: localStorage.getItem(KEYS.accessToken),
    refreshToken: localStorage.getItem(KEYS.refreshToken),
    expiry: (() => {
      const raw = localStorage.getItem(KEYS.expiry);
      return raw ? Number(raw) : null;
    })(),
  };
}

export function clearTokens(): void {
  Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
}

export function isTokenExpired(idToken: string): boolean {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    // Add a 30-second buffer to account for clock skew
    return payload.exp * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
}

export function decodeIdToken(idToken: string): Record<string, any> | null {
  try {
    return JSON.parse(atob(idToken.split('.')[1]));
  } catch {
    return null;
  }
}
